import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Target, TrendingUp, Users, Shield, Compass, Award, Zap } from 'lucide-react';
export function LeadershipPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-928" data-mp="2pby5ew0m4ce6">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-929" data-mp="2law6yheis4d9">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-930" data-mp="2gbsyemzbzg0j">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-931" data-mp="26jx1hf8zdvxw">
            <span className="w-8 h-px bg-teal" data-id="element-932" data-mp="21o7wuxi3touq"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-933" data-mp="1xlbjbl7f8ixn">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-934" data-mp="1sn9qi8k7158z"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-935" data-mp="gl3m47tcxsmz">
            Leadership Development and Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-936" data-mp="ldepjknb3wka">
            SyNeural's platform architecture is designed to help organizations
            and institutions build stronger leadership by focusing on the
            capabilities that matter most when responsibility increases.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-937" data-mp="148brepqsd1p0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-938" data-mp="q9vdoeuiaaxd">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-939" data-mp="3g1a1m1yvg0bi">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying readiness gaps, and
            connecting those gaps to meaningful pathways for growth,
            development, and higher responsibility over time.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-940" data-mp="17t56du0vspqm">
            Leadership is often evaluated through incomplete signals such as
            charisma, seniority, credentials, confidence, or visibility. But as
            responsibility rises, those signals become less reliable on their
            own. The real demands of leadership involve reasoning under
            pressure, consequence awareness, emotional stability, communication,
            systems thinking, responsibility at scale, and the ability to make
            decisions without destabilizing the institution or environment being
            led. SyNeural helps make those capabilities more visible and more
            actionable.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-941" data-mp="9y15ddoh6mib">
        {/* Leadership Readiness */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-942" data-mp="equeedqz8h07">
          <div className="flex items-center gap-4 mb-6" data-id="element-943" data-mp="3n5mgce532nus">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-944" data-mp="3r6g1izr55ojz">
              <Target size={28} data-id="element-945" data-mp="1pqqh1exkkp" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-946" data-mp="3w3oe8ktit0du">
              Leadership Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-947" data-mp="1bf0jt2vq3dfj">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-948" data-mp="19xuwl0lteals">
              One of SyNeural's core strengths is its ability to help
              distinguish between status and readiness. Leadership readiness is
              not just about ambition or presentation. It is about whether an
              individual can absorb complexity, think through tradeoffs,
              regulate emotion under pressure, communicate clearly, and carry
              broader consequences responsibly. The platform helps create a more
              structured way to assess and develop that readiness over time.
            </p>
          </div>
        </motion.div>

        {/* From Potential to Responsibility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-949" data-mp="1xnz0l92x0cdb">
          <div className="flex items-center gap-4 mb-6" data-id="element-950" data-mp="2qbqj1esv7jqe">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-951" data-mp="2v9s1pymdph1c">
              <TrendingUp size={28} data-id="element-952" data-mp="2hak1d2ehc2vk" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-953" data-mp="2h9r0uj6469lv">
              From Potential to Responsibility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-954" data-mp="27jxrlotg26ks">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-955" data-mp="2cbfvolqrkn9z">
              Many individuals show promise long before they are fully ready for
              greater responsibility. SyNeural helps make that transition more
              intentional by identifying strengths, bottlenecks, and
              developmental priorities that influence whether potential can
              mature into reliable leadership. This makes the platform useful
              not only for evaluating who is ready now, but for developing who
              could become ready with the right support and structure.
            </p>
          </div>
        </motion.div>

        {/* Decision-Making Under Pressure */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-956" data-mp="3ljas47vbzc25">
          <div className="flex items-center gap-4 mb-6" data-id="element-957" data-mp="d3r1zhpyi9rb">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-958" data-mp="d267fs3n6qtg">
              <Zap size={28} data-id="element-959" data-mp="43kp9y231dt7" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-960" data-mp="42ston8hjg2x">
              Decision-Making Under Pressure
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-961" data-mp="2ottf17u0qnqf">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-962" data-mp="14bhcx2wx71mb">
              As leadership stakes rise, decision-making under pressure becomes
              increasingly important. SyNeural can support development in areas
              such as emotional regulation, consequence reasoning, systems
              thinking, communication in high-stress settings, and the ability
              to maintain stability amid complexity and conflict. This makes the
              platform especially useful in environments where leadership
              failures can create broad institutional, civic, or organizational
              consequences.
            </p>
          </div>
        </motion.div>

        {/* Leadership Development Pathways */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-963" data-mp="210gqmxg9wvlw">
          <div className="flex items-center gap-4 mb-6" data-id="element-964" data-mp="3jvbgszacce5z">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-965" data-mp="3eybbiqlbfffx">
              <Compass size={28} data-id="element-966" data-mp="3so4yt4h69rnj" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-967" data-mp="3sr0zv1obgwg7">
              Leadership Development Pathways
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-968" data-mp="30ytfbq5owzwj">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-969" data-mp="2vzyxorjglhe0">
              A core principle of the SyNeural platform is that every meaningful
              weakness should be paired with a pathway. In leadership contexts,
              this means developmental gaps should not simply disqualify a
              person from future growth. They should also help define what
              stronger readiness would require and how it can be built. SyNeural
              supports this by translating insight into practical development
              pathways tied to coaching, training, mentorship, structured
              experience, and institutional support.
            </p>
          </div>
        </motion.div>

        {/* Institutional Leadership */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-970" data-mp="nramv9jajui9">
          <div className="flex items-center gap-4 mb-6" data-id="element-971" data-mp="11rcywf4x3tpl">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-972" data-mp="11qthr0n3d9cm">
              <Shield size={28} data-id="element-973" data-mp="16o3ihcfxl7fr" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-974" data-mp="1uepnsvyjwdm2">
              Institutional Leadership and Responsibility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-975" data-mp="12mtnpnuctdnc">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-976" data-mp="12l0jef77r5ii">
              Leadership inside institutions requires more than decisiveness. It
              requires the ability to manage complexity, absorb dissent, align
              action with long-term goals, and act with responsibility toward
              the larger system rather than personal ego alone. SyNeural helps
              organizations better understand and develop these qualities in
              people moving into management, executive, civic, or public-facing
              leadership roles.
            </p>
          </div>
        </motion.div>

        {/* Leadership Pipelines */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-977" data-mp="exscjsu4zoed">
          <div className="flex items-center gap-4 mb-6" data-id="element-978" data-mp="3igf2fooazutm">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-979" data-mp="3dhvdrgibs4xk">
              <Users size={28} data-id="element-980" data-mp="3sfdy86nyzvz7" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-981" data-mp="3sbzzr9ifg6x1">
              Leadership Pipelines and Succession
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-982" data-mp="58bxxzcf3tcx">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-983" data-mp="9imecgoycfe">
              Organizations and institutions often struggle with succession
              because they lack a clear framework for identifying who is
              developing into leadership readiness and what development still
              needs to happen. SyNeural helps make leadership pipelines more
              structured by showing where readiness is strong, where it is still
              forming, and how targeted support can improve long-term leadership
              depth. This makes it relevant for succession planning, advancement
              pathways, and institutional continuity.
            </p>
          </div>
        </motion.div>

        {/* Executive and High-Responsibility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-984" data-mp="15qdl0jhjipz9">
          <div className="flex items-center gap-4 mb-6" data-id="element-985" data-mp="1jpv9exqxc183">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-986" data-mp="1joby7lk4kgd6">
              <Crown size={28} data-id="element-987" data-mp="3lhrpmko1dz97" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-988" data-mp="3ljch21f2122u">
              Executive and High-Responsibility Environments
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-989" data-mp="27m9ptdk1u5wg">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-990" data-mp="22ui0f6cmq67w">
              At higher levels of leadership, poor judgment can create
              large-scale consequences. SyNeural is designed to become more
              rigorous as the stakes of decision-making rise. In executive,
              senior institutional, and public-sector environments, the platform
              becomes especially relevant for evaluating consequence reasoning,
              crisis stability, communication under pressure, governance
              readiness, and broader responsibility at scale. This allows
              leadership development to become more disciplined and less
              dependent on impression management alone.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Leadership Capabilities */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-991" data-mp="114whrbby67kq">
        <div className="max-w-5xl mx-auto" data-id="element-992" data-mp="2p1kgwnzfyixl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-993" data-mp="2p0j7eny2q4hz">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-994" data-mp="38redrgh0jo8o">
              Key Leadership Capabilities
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-995" data-mp="3cpluwcfe2rke">
              {[{
              icon: <Target size={20} data-id="element-996" data-mp="33qqn4vwp59ez" />,
              label: 'Reasoning Under Pressure'
            }, {
              icon: <Compass size={20} data-id="element-997" data-mp="1nbfd73dtbfs6" />,
              label: 'Consequence Awareness'
            }, {
              icon: <Shield size={20} data-id="element-998" data-mp="1s9qolijr9yci" />,
              label: 'Emotional Stability'
            }, {
              icon: <Users size={20} data-id="element-999" data-mp="1fu0qasw87h9b" />,
              label: 'Communication Clarity'
            }, {
              icon: <Award size={20} data-id="element-1000" data-mp="1tryz7irs2lhf" />,
              label: 'Systems Thinking'
            }, {
              icon: <Crown size={20} data-id="element-1001" data-mp="1os2utif64s1c" />,
              label: 'Responsibility at Scale'
            }].map((item, index) => <motion.div key={item.label} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="bg-white p-6 rounded-lg border border-line" data-id="element-1002" data-mp="x3sr2lvndaye" data-mpi={index}>
                  <div className="w-10 h-10 rounded-md bg-navy/10 flex items-center justify-center text-navy mb-3" data-id="element-1003" data-mp="2ifl1kvpicxn3">
                    {item.icon}
                  </div>
                  <div className="text-sm font-medium text-ink-dark" data-id="element-1004" data-mp="2s667l47ih4g1">
                    {item.label}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-1005" data-mp="1zhqx8uzbrab1">
        <div className="max-w-4xl mx-auto" data-id="element-1006" data-mp="24fsfxesu97lz">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1007" data-mp="28i6x7uguzzb5">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-1008" data-mp="188xgzwuakdgl">
              Why It Matters for Leadership Development
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-1009" data-mp="14ahvjxduzd5a">
              Leadership is one of the most consequential forms of human
              capability because leadership judgment scales through
              institutions, organizations, and communities. SyNeural helps
              create a more structured and developmental way to understand that
              capability. It supports the long-term goal of building leaders who
              are not simply visible or ambitious, but genuinely prepared to
              carry complexity, responsibility, and consequence with greater
              competence over time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}